import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Collapse} from 'src/design/atoms/Collapse';
import {Loader} from 'src/design/atoms/Loader';
import {Text} from 'src/design/atoms/Text';
import {IconChevronDown, IconChevronUp} from '@tabler/icons-react';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo, useState} from 'react';
import type {PersonnelImportColumnResponse} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {useGetPersonnelImportColumnsQuery} from 'src/redux/queries/personnel';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export const ColumnGuide: React.FC = () => {
  const [opened, setOpened] = useState(false);
  const {data, isLoading, isError, error} =
    useGetPersonnelImportColumnsQuery();

  const columns = useMemo<MRT_ColumnDef<PersonnelImportColumnResponse>[]>(
    () => [
      {
        id: 'name',
        header: 'Column',
        accessorFn: row => row.name,
        Cell: ({row}) => (
          <Box>
            <Text size="sm">{row.original.name}</Text>
            {row.original.aliases.length > 0 && (
              <Text size="xs" c="dimmed">
                Also accepts: {row.original.aliases.join(', ')}
              </Text>
            )}
          </Box>
        ),
      },
      {
        id: 'required',
        header: 'Required',
        accessorFn: row => (row.required ? 'Required' : 'Optional'),
      },
      {
        id: 'acceptedValues',
        header: 'Accepted values',
        accessorFn: row => row.acceptedValues,
      },
      {
        id: 'example',
        header: 'Example',
        accessorFn: row => row.example,
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'personnel-import-column-guide',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 25},
    defaultShowColumnFilters: false,
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: false,
      manualFiltering: false,
      manualPagination: false,
      columns,
      data: data ?? [],
      getRowId: row => row.name,
      enableRowActions: false,
      enableGlobalFilter: false,
      enableColumnFilters: false,
      enableTopToolbar: false,
      enableBottomToolbar: false,
      enableColumnActions: false,
    },
    totalCount: data?.length ?? 0,
    isError: false,
    isLoading: false,
    isFetching: false,
  });

  const renderBody = () => {
    if (isLoading) {
      return (
        <Center py="md">
          <Loader size="sm" />
        </Center>
      );
    }

    if (isError) {
      return <ErrorAlert message={parseErrorMessage(error)} />;
    }

    if (!data || data.length === 0) {
      return (
        <Text size="sm" c="dimmed">
          The column reference is unavailable right now. Download the template
          to see the expected columns.
        </Text>
      );
    }

    return table;
  };

  return (
    <Box>
      <Button
        variant="subtle"
        size="compact-sm"
        onClick={() => setOpened(current => !current)}
        rightSection={
          opened ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />
        }>
        {opened ? 'Hide column guide' : 'Show column guide'}
      </Button>
      <Collapse in={opened}>
        <Box mt="sm">{renderBody()}</Box>
      </Collapse>
    </Box>
  );
};
